'use client';
import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { isValidLang } from '@/lib/i18n/resolveLang';
import type { Translations } from '@/lib/i18n/translations';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import ButtonRow from '@/components/ui/ButtonRow';

// Deliberately narrow — this is exactly (and only) the shape
// get_public_booking_page returns. No id/slug/owner/subscription/staff
// field exists here because the RPC never returns one; the business's
// internal UUID never reaches the browser at all.
interface Business {
  name: string;
  description?: string;
  phone?: string;
  address?: string;
  city?: string;
  category?: string;
  logo_url?: string;
}
interface Service {
  id: string;
  name: string;
  duration_minutes: number;
  price: number | null;
  description?: string;
}
// One row per candidate slot for a given day, as returned by
// get_public_availability (slot_date/slot_time/available), keyed by date
// in availabilityMap below.
interface AvailabilitySlot {
  time: string;
  available: boolean;
}
interface FoundBooking {
  booking_ref: string;
  client_name: string;
  service_name: string;
  booking_date: string;
  booking_time: string;
}

const STEPS = ['service', 'datetime', 'info'] as const;
type Step = (typeof STEPS)[number];

function mapBookingError(errors: Translations['booking']['errors'], message?: string): string {
  if (!message) return errors.generic;
  const map: Record<string, string> = {
    no_availability: errors.noAvailability,
    business_closed: errors.businessClosed,
    business_closed_that_day: errors.businessClosedThatDay,
    outside_business_hours: errors.outsideBusinessHours,
    date_in_past: errors.dateInPast,
    // Same error code create_booking_by_owner already used for this
    // exact concept (Phase 6) — reused here rather than inventing a
    // second synonym, now that create_booking raises it too.
    slot_in_past: errors.slotInPast,
    business_not_found: errors.businessNotFound,
    service_not_found: errors.serviceNotFound,
    client_name_required: errors.clientNameRequired,
    client_email_required: errors.clientEmailRequired,
    client_phone_required: errors.clientPhoneRequired,
    // Defense-in-depth: the UI already prevents submission when
    // accepting_bookings is false, but a stale page or a direct RPC
    // caller could still hit create_booking's/join_waiting_list's hard
    // gate. Never surface the raw code — same neutral copy either way,
    // no mention of trial/payment/subscription/test status.
    subscription_required: errors.bookingsUnavailable,
    // replace_public_booking's own error codes (earlier-slot
    // replacement flow only).
    booking_not_found: errors.bookingNotFound,
    original_booking_not_active: errors.originalBookingNotActive,
    original_booking_not_in_future: errors.originalBookingNotInFuture,
    no_eligible_earlier_slot_preference: errors.noEligibleEarlierSlotPreference,
    new_slot_not_earlier: errors.newSlotNotEarlier,
    replacement_already_in_progress: errors.replacementAlreadyInProgress,
  };
  return map[message] || message;
}

// Minutes-since-midnight, never a raw string comparison — availabilityMap's
// own slot times are already truncated to 5-char "HH:MM" (see the
// get_public_availability load effect below), but a TIME column value
// fetched elsewhere (e.g. get_replaceable_booking_context's
// current_booking_time) is not guaranteed to be, and a "HH:MM" vs
// "HH:MM:SS" length mismatch compares incorrectly as plain strings.
const timeToMinutes = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

function BusinessBookingPageContent() {
  const params = useParams();
  const slug = params?.slug as string;
  const searchParams = useSearchParams();
  const supabase = createClient();
  const router = useRouter();
  const { lang, setLang, t } = useLanguage();
  const tb = t.booking;

  // Booking pages are the one place that also resolve `lang` from the URL,
  // synchronizing it into the shared/global preference. A valid query
  // param always wins and becomes the new remembered preference.
  const langParam = searchParams?.get('lang');
  useEffect(() => {
    if (isValidLang(langParam) && langParam !== lang) {
      setLang(langParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langParam]);

  // A confirmation email's cancellation button links here with
  // ?cancel=1&ref=<booking_ref> — never the customer's email, which
  // they still have to type themselves in the existing modal below.
  const cancelParam = searchParams?.get('cancel');
  const refParam = searchParams?.get('ref');
  useEffect(() => {
    if (cancelParam === '1' && refParam) {
      setCancelBookingRef(refParam);
      setShowCancelModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelParam, refParam]);

  // An earlier-slot email's "Shiko oraret" button links here with
  // ?rebook_ref=<original_booking_ref> — same bare-ref-only convention
  // as ?cancel=1&ref=, never the customer's email. When present, the
  // customer must first verify the email on that booking (below) before
  // the normal service/date/info flow renders at all — this both
  // resolves+locks the ORIGINAL service (get_replaceable_booking_context
  // never trusts a client-supplied service_id) and re-uses this page's
  // existing "never pre-fill PII from a bare ref" convention. Once
  // verified, the flow continues into the existing datetime/info steps
  // unchanged, with the service step skipped entirely (there is nothing
  // to choose — see rebookVerified below); only the final submit targets
  // replace_public_booking instead of create_public_booking. Read once
  // on mount — this page is always freshly loaded from the email link,
  // never navigated to internally with a changing rebook_ref.
  const rebookRef = searchParams?.get('rebook_ref') || null;
  const [rebookEmailInput, setRebookEmailInput] = useState('');
  const [rebookVerifying, setRebookVerifying] = useState(false);
  const [rebookError, setRebookError] = useState('');
  const [rebookVerified, setRebookVerified] = useState(false);
  // The original booking's own date/time, from get_replaceable_booking_
  // context — drives the UX-only "don't even offer a non-earlier slot"
  // filtering below. replace_public_booking still re-validates
  // strictly-earlier server-side regardless; this is display alignment
  // only, never a substitute for that check.
  const [rebookOriginal, setRebookOriginal] = useState<{ date: string; time: string } | null>(null);

  const [business, setBusiness] = useState<Business | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  // Coarse, server-computed booking-eligibility signal from
  // get_public_booking_page (public.is_business_booking_enabled under the
  // hood) — never the raw subscription_status/trial dates/is_test_business
  // themselves, which this page never receives at all. Defaults to true so
  // the loading spinner doesn't briefly flash the unavailable panel before
  // the first response arrives.
  const [acceptingBookings, setAcceptingBookings] = useState(true);
  // Keyed by 'YYYY-MM-DD', one entry per candidate slot for that day, as
  // returned by get_public_availability for the currently selected
  // service + visible month. A date with no key at all means the RPC
  // produced zero rows for it (closed / inside a closure range) — a date
  // present but with every entry available:false means open but fully
  // booked. This distinction reproduces the same three-way calendar
  // legend (closed/grey, full/red, has slots/green) that the old
  // client-side schedule/closure computation used to produce.
  const [availabilityMap, setAvailabilityMap] = useState<Map<string, AvailabilitySlot[]>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [step, setStep] = useState<Step>('service');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [showWaitlistPrompt, setShowWaitlistPrompt] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ name: '', email: '', phone: '' });
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const [lastBookingRef, setLastBookingRef] = useState<string | null>(null);
  // 'confirmed' | 'pending' — from create_public_booking's own new
  // status column (auto-confirm workflow), never assumed client-side.
  const [lastBookingStatus, setLastBookingStatus] = useState<string | null>(null);
  const [showEarlierSlotPrompt, setShowEarlierSlotPrompt] = useState(false);
  const [earlierSlotSubmitting, setEarlierSlotSubmitting] = useState(false);
  const [earlierSlotDone, setEarlierSlotDone] = useState(false);

  // Customer cancellation — Booking ID + email (secondary method)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelBookingRef, setCancelBookingRef] = useState('');
  const [cancelEmail, setCancelEmail] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelConfirmStep, setCancelConfirmStep] = useState(false);
  const [foundBooking, setFoundBooking] = useState<FoundBooking | null>(null);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.rpc('get_public_booking_page', { p_slug: slug });
      if (error || !data || data.length === 0) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const first = data[0];
      setBusiness({
        name: first.business_name,
        category: first.business_category || undefined,
        phone: first.business_phone || undefined,
        address: first.business_address || undefined,
        city: first.business_city || undefined,
        description: first.business_description || undefined,
        logo_url: first.business_logo_url || undefined,
      });
      setAcceptingBookings(first.accepting_bookings !== false);
      setServices(
        data
          .filter((row: any) => row.service_id)
          .map((row: any) => ({
            id: row.service_id,
            name: row.service_name,
            duration_minutes: row.service_duration_minutes,
            price: row.service_price,
            description: row.service_description || undefined,
          }))
      );
      setLoading(false);
    };
    if (slug) load();
  }, [slug]);

  // Availability is fetched through a SECURITY DEFINER RPC that already
  // computes the final available/not-available decision server-side (no
  // raw schedule/staff/leave/closure data, no client PII) — refreshed
  // whenever the visible month or the selected service changes, since
  // the RPC is parameterized by service.
  useEffect(() => {
    const loadAvailability = async () => {
      // No point requesting/holding slot data the customer can never
      // book anyway — the actual security gate is create_booking itself,
      // this is purely to avoid an unnecessary round trip.
      if (!business || !selectedService || !acceptingBookings) {
        setAvailabilityMap(new Map());
        return;
      }
      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth();
      // Build the range from local Y/M/D components directly — never through
      // toISOString(), which converts local midnight to UTC and silently
      // shifts the date back a day for any timezone ahead of UTC (e.g.
      // Europe/Tirane), cutting the real last day of the month out of range.
      const lastDateOfMonth = new Date(year, month + 1, 0).getDate();
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDateOfMonth).padStart(2, '0')}`;
      const { data } = await supabase.rpc('get_public_availability', {
        p_slug: slug,
        p_service_id: selectedService.id,
        p_start_date: start,
        p_end_date: end,
      });
      const map = new Map<string, AvailabilitySlot[]>();
      (data || []).forEach((row: any) => {
        const dateStr: string = row.slot_date;
        const timeStr: string = (row.slot_time || '').substring(0, 5);
        if (!map.has(dateStr)) map.set(dateStr, []);
        map.get(dateStr)!.push({ time: timeStr, available: row.available });
      });
      setAvailabilityMap(map);
    };
    loadAvailability();
  }, [slug, business, selectedService?.id, calendarMonth, acceptingBookings]);

  // A date absent from availabilityMap means the RPC produced zero rows
  // for it (closed / inside a closure range) — distinct from a date
  // present with every slot available:false (open but fully booked).
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!business || !selectedService || !selectedDate || !selectedTime) return;
    // Native `required` on the phone input rejects an empty value but not
    // a whitespace-only one (e.g. "   ") — the backend already rejects
    // that via btrim() in create_booking, this just surfaces the same
    // existing translated message before making the round trip.
    if (!form.phone.trim()) {
      setSubmitError(tb.errors.clientPhoneRequired);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      // Staff assignment happens invisibly, server-side, inside these
      // RPCs — the customer never picks or sees a staff member. Both
      // also re-validate availability atomically, so this is safe even
      // if two customers submit for the same slot at the same time.
      //
      // rebookRef present: this is an earlier-slot replacement, not an
      // unrelated new booking. replace_public_booking resolves the
      // ORIGINAL booking itself server-side from rebookRef + the
      // email entered above, and derives its service/name/phone/lang
      // from that original booking — it never accepts them from this
      // form, so a replacement can never end up with a different
      // service or a silently-changed customer identity. Nothing is
      // cancelled client-side here or anywhere else in this flow; the
      // old booking's cancellation (when it happens at all) is entirely
      // the RPC's own doing, atomically with the new booking.
      const { data, error } = rebookRef
        ? await supabase.rpc('replace_public_booking', {
            p_slug: slug,
            p_booking_ref: rebookRef,
            p_client_email: form.email,
            p_booking_date: selectedDate,
            p_booking_time: selectedTime,
          })
        : await supabase.rpc('create_public_booking', {
            p_slug: slug,
            p_service_id: selectedService.id,
            p_booking_date: selectedDate,
            p_booking_time: selectedTime,
            p_client_name: form.name,
            p_client_email: form.email,
            p_client_phone: form.phone.trim() || null,
            p_notes: form.notes || null,
            // Presentation-only preference for the customer confirmation
            // email — server-normalized to exactly 'sq' or 'en' inside
            // create_booking (any other value silently becomes 'sq'). Never
            // used to pick a recipient or any booking-content field.
            p_client_lang: lang,
          });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) throw new Error('no_availability');

      setLastBookingRef(row.booking_ref || null);
      setLastBookingStatus(row.status || null);

      // Customer booking-confirmation/pending-acknowledgement email is
      // never triggered from the browser — create_public_booking →
      // create_booking enqueues the correct one (confirmed or pending,
      // per the business's own auto_confirm_bookings setting) directly
      // into booking_notification_deliveries, sent by the same trusted,
      // service_role-only worker that already handles every other
      // notification type. The browser has no ability to influence the
      // recipient or content of that email; it only supplied
      // p_client_lang above, which is presentation-only and
      // server-validated.
      //
      // The earlier-slot waitlist prompt only makes sense once a
      // booking is actually locked in — offering "want an earlier slot
      // than this one?" before the business has even confirmed a
      // pending request would be confusing, so that step is skipped
      // for a pending booking and the customer goes straight to the
      // (pending-worded) confirmation screen.
      if (row.status === 'pending') {
        redirectToConfirmation(row.status);
      } else {
        setShowEarlierSlotPrompt(true);
      }
    } catch (err: any) {
      setSubmitError(mapBookingError(tb.errors, err?.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Only reachable when rebookRef is set. Resolves and LOCKS the
  // original booking's own service server-side — this is the only
  // place selectedService is ever set for a replacement, so the
  // customer can never end up viewing/booking a different service than
  // the one replace_public_booking will actually use. Advances straight
  // to the datetime step; the free-choice service step is never shown
  // for this flow (see the step==='service' guard below).
  const handleVerifyRebookEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rebookRef || !rebookEmailInput.trim()) return;
    setRebookVerifying(true);
    setRebookError('');
    try {
      const { data, error } = await supabase.rpc('get_replaceable_booking_context', {
        p_slug: slug,
        p_booking_ref: rebookRef,
        p_client_email: rebookEmailInput.trim(),
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) throw new Error('booking_not_found');
      setSelectedService({
        id: row.service_id,
        name: row.service_name,
        duration_minutes: row.service_duration_minutes,
        price: null,
        description: undefined,
      });
      // current_booking_time is a Postgres TIME value — truncate to
      // 5-char "HH:MM" up front, matching availabilityMap's own slot
      // times (see the load effect below), so every later comparison
      // is between same-shaped values.
      setRebookOriginal({
        date: row.current_booking_date,
        time: (row.current_booking_time || '').substring(0, 5),
      });
      // The verified email becomes the form's own email — re-typed by
      // the customer, never silently substituted — so it's already
      // correct when replace_public_booking re-verifies it at final
      // submit.
      setForm((f) => ({ ...f, email: rebookEmailInput.trim() }));
      setRebookVerified(true);
      setStep('datetime');
    } catch (err: any) {
      setRebookError(mapBookingError(tb.errors, err?.message));
    } finally {
      setRebookVerifying(false);
    }
  };

  const handleEarlierSlotYes = async () => {
    // join_earlier_slot_waitlist resolves the booking itself server-side
    // from booking_ref + email — no business/service id is sent or needed.
    if (!lastBookingRef || !form.email) return;
    setEarlierSlotSubmitting(true);
    try {
      await supabase.rpc('join_earlier_slot_waitlist', {
        p_booking_ref: lastBookingRef,
        p_client_email: form.email,
      });
    } catch {
      // Non-blocking
    } finally {
      setEarlierSlotSubmitting(false);
      setEarlierSlotDone(true);
      setTimeout(() => redirectToConfirmation(), 1200);
    }
  };

  const handleEarlierSlotNo = () => redirectToConfirmation();

  // Accepts an explicit status so the call made synchronously right
  // after create_public_booking resolves (in handleSubmit, above) uses
  // the value just returned by the RPC rather than the lastBookingStatus
  // state var, which would still read its pre-update value in that same
  // synchronous block — React state updates are not immediately visible
  // to code that runs right after the setState call that scheduled them.
  // The other two call sites (earlier-slot yes/no) run later, after a
  // render has already committed, so the state var is reliable there.
  const redirectToConfirmation = (statusOverride?: string | null) => {
    if (!business || !selectedService) return;
    // No business_id/service_id/booking id here — booking-confirmation
    // relies on booking_ref alone; no internal UUID is carried in the URL.
    const qParams = new URLSearchParams({
      business: business.name,
      slug,
      service: selectedService.name,
      price: selectedService.price?.toString() || '',
      duration: selectedService.duration_minutes.toString(),
      date: selectedDate,
      time: selectedTime,
      name: form.name,
      email: form.email,
      phone: form.phone,
      booking_ref: lastBookingRef || '',
      status: statusOverride ?? lastBookingStatus ?? 'confirmed',
      lang,
    });
    router.push(`/booking-confirmation?${qParams.toString()}`);
  };

  const handleWaitlistSubmit = async () => {
    if (!business || !selectedService || !waitlistForm.name || !waitlistForm.email) return;
    setWaitlistSubmitting(true);
    try {
      await supabase.rpc('join_waiting_list', {
        p_slug: slug,
        p_service_id: selectedService.id,
        p_client_name: waitlistForm.name,
        p_client_email: waitlistForm.email,
        p_client_phone: waitlistForm.phone || null,
        p_preferred_date: selectedDate || null,
      });
      setWaitlistSuccess(true);
    } catch {
      // Non-blocking
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Cancellation by Booking ID + email — routed through narrow RPCs that
  // only ever touch the one matching row, never the raw table.
  const handleFindBooking = async () => {
    if (!business || !cancelBookingRef || !cancelEmail) return;
    setCancelLoading(true);
    setCancelError('');
    try {
      const { data, error } = await supabase.rpc('find_booking_for_cancellation', {
        p_booking_ref: cancelBookingRef,
        p_client_email: cancelEmail,
      });
      const row = Array.isArray(data) ? data[0] : data;
      if (error || !row) {
        setCancelError(tb.cancelModal.notFound);
        return;
      }
      setFoundBooking(row);
      setCancelConfirmStep(true);
    } catch {
      setCancelError(tb.cancelModal.genericError);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!foundBooking) return;
    setCancelLoading(true);
    setCancelError('');
    try {
      const { data, error } = await supabase.rpc('cancel_booking_by_ref', {
        p_booking_ref: foundBooking.booking_ref,
        p_client_email: cancelEmail,
      });
      if (error || !data) throw error || new Error('cancel_failed');
      setCancelSuccess(true);
    } catch {
      setCancelError(tb.cancelModal.cancelFailed);
    } finally {
      setCancelLoading(false);
    }
  };

  const currentStepIndex = STEPS.indexOf(step);

  if (loading)
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0015, #1a0035)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '3px solid rgba(124,58,237,0.3)',
              borderTop: '3px solid #7C3AED',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>{tb.loading}</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );

  if (notFound)
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0015, #1a0035)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '64px', marginBottom: '16px' }}>🔍</p>
          <h1 style={{ color: '#fff', fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
            {tb.notFoundTitle}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>{tb.notFoundBody}</p>
          <Link href="/" style={{ color: '#a78bfa', textDecoration: 'none', fontSize: '15px' }}>
            ← {tb.backToHome}
          </Link>
        </div>
      </div>
    );

  // Booking confirmed screen (earlier-slot opt-in prompt)
  if (showEarlierSlotPrompt) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0015 0%, #1a0035 50%, #0d001f 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 16px',
        }}
      >
        <style>{`
          @media (max-width: 480px) { .rezervo-btn-row { flex-direction: column; } }
        `}</style>
        <div style={{ maxWidth: '480px', width: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px',
            }}
          >
            <Link href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontSize: '16px', fontWeight: 700, color: 'rgba(255,255,255,0.35)' }}>
                Rezervo <span style={{ color: '#7C3AED' }}>Online</span>
              </span>
            </Link>
            <LanguageSwitcher />
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '36px 28px',
              backdropFilter: 'blur(20px)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(52,211,153,0.2)',
                border: '2px solid rgba(52,211,153,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                margin: '0 auto 20px',
              }}
            >
              ✅
            </div>
            <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
              {tb.earlierSlot.confirmedTitle}
            </h2>
            {lastBookingRef && (
              <div
                style={{
                  background: 'rgba(124,58,237,0.12)',
                  border: '1px solid rgba(124,58,237,0.25)',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  marginBottom: '12px',
                  display: 'inline-block',
                }}
              >
                <span
                  style={{
                    color: '#a78bfa',
                    fontWeight: 700,
                    fontSize: '15px',
                    fontFamily: 'monospace',
                    letterSpacing: '0.08em',
                  }}
                >
                  {lastBookingRef}
                </span>
              </div>
            )}
            <div
              style={{
                background: 'rgba(124,58,237,0.1)',
                border: '1px solid rgba(124,58,237,0.2)',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '24px',
                textAlign: 'left',
              }}
            >
              <p style={{ color: '#a78bfa', fontSize: '13px', margin: 0 }}>
                ✂️ {selectedService?.name}
              </p>
              <p style={{ color: '#a78bfa', fontSize: '13px', marginTop: '4px' }}>
                📅 {selectedDate} • {selectedTime}
              </p>
            </div>
            {earlierSlotDone ? (
              <div style={{ color: '#34d399', fontSize: '15px', fontWeight: 600 }}>
                ✓ {tb.earlierSlot.done}
              </div>
            ) : (
              <>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '15px',
                    lineHeight: 1.6,
                    marginBottom: '24px',
                  }}
                >
                  {tb.earlierSlot.question}
                </p>
                <ButtonRow>
                  <button
                    onClick={handleEarlierSlotYes}
                    disabled={earlierSlotSubmitting}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '13px 20px',
                      color: '#fff',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: earlierSlotSubmitting ? 'not-allowed' : 'pointer',
                      opacity: earlierSlotSubmitting ? 0.7 : 1,
                    }}
                  >
                    {earlierSlotSubmitting
                      ? tb.earlierSlot.savingEllipsis
                      : tb.earlierSlot.yesNotifyMe}
                  </button>
                  <button
                    onClick={handleEarlierSlotNo}
                    style={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '12px',
                      padding: '13px 20px',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '15px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {t.common.no}
                  </button>
                </ButtonRow>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Rebook-only UX filter: on the ORIGINAL booking's own date, never
  // offer a slot at or after its time — the server (replace_public_
  // booking) rejects those anyway, this just avoids showing the
  // customer a choice that could only fail. Every other date is
  // returned unfiltered (dates after the original date are excluded
  // entirely from the calendar itself, below, and earlier dates need no
  // per-slot filtering at all). Minutes-based comparison throughout —
  // never a raw string compare — so a length mismatch between
  // availabilityMap's own 5-char slot times and rebookOriginal.time
  // can't silently misorder them.
  const getRebookEffectiveSlots = (dateStr: string, slots: AvailabilitySlot[]): AvailabilitySlot[] => {
    if (!rebookRef || !rebookOriginal || dateStr !== rebookOriginal.date) return slots;
    const cutoff = timeToMinutes(rebookOriginal.time);
    return slots.filter((sl) => timeToMinutes(sl.time) < cutoff);
  };

  // Explicit three-way state for the selected date, so "no date chosen
  // yet", "has available slots", and "has slot rows but every one is
  // booked" are never conflated with each other or with a closed/
  // non-working day (which has zero rows and must not trigger the
  // waitlist panel either — see selectedDateIsFullyBooked below).
  const timeSlots =
    step === 'datetime' && selectedDate && selectedService
      ? getRebookEffectiveSlots(selectedDate, availabilityMap.get(selectedDate) || [])
          .slice()
          .sort((a, b) => a.time.localeCompare(b.time))
      : [];
  const selectedDateHasAvailableSlots = timeSlots.some((slot) => slot.available);
  // Fully booked requires an actual selected date with actual slot rows
  // for it — a date with zero rows (closed/non-working, or simply not
  // selected yet) is never "fully booked", it's a different state.
  const selectedDateIsFullyBooked =
    Boolean(selectedDate) && timeSlots.length > 0 && !selectedDateHasAvailableSlots;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0015 0%, #1a0035 50%, #0d001f 100%)',
        padding: '24px 16px',
      }}
    >
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .slot-btn:hover { opacity: 0.85; transform: scale(1.03); }
        .svc-btn:hover { border-color: rgba(124,58,237,0.4) !important; background: rgba(124,58,237,0.08) !important; }
        .cal-day:hover { border-color: rgba(124,58,237,0.4) !important; }
        @media (max-width: 480px) { .rezervo-btn-row { flex-direction: column; } }
      `}</style>
      <div style={{ maxWidth: '580px', margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '28px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <Link href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: '16px', fontWeight: 700, color: 'rgba(255,255,255,0.35)' }}>
              Rezervo <span style={{ color: '#7C3AED' }}>Online</span>
            </span>
          </Link>
          <LanguageSwitcher />
        </div>

        {/* Business card */}
        <div
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '20px',
            padding: '24px',
            marginBottom: '20px',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                fontWeight: 800,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {business?.name.charAt(0)}
            </div>
            <div>
              <h1 style={{ color: '#fff', fontSize: '19px', fontWeight: 700, margin: 0 }}>
                {business?.name}
              </h1>
              {business?.category && (
                <p style={{ color: '#a78bfa', fontSize: '13px', margin: '2px 0 0' }}>
                  {business.category}
                </p>
              )}
              {(business?.city || business?.address) && (
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', margin: '2px 0 0' }}>
                  📍 {[business.address, business.city].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </div>
          {business?.description && (
            <p
              style={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: '13px',
                marginTop: '14px',
                lineHeight: 1.6,
              }}
            >
              {business.description}
            </p>
          )}
        </div>

        {!acceptingBookings ? (
          // Business info stays visible above; the interactive booking
          // flow itself is replaced by a neutral notice. Never mentions
          // trial/payment/subscription/test status — customers only ever
          // see this one sentence, regardless of the underlying reason.
          <div
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              backdropFilter: 'blur(20px)',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px', lineHeight: 1.6 }}>
              {tb.errors.bookingsUnavailable}
            </p>
          </div>
        ) : rebookRef && !rebookVerified ? (
          // Replacement flow, email not yet verified: get_replaceable_
          // booking_context has not resolved a service yet, so nothing
          // of the normal service/date/info flow can render at all —
          // there is no selectedService to lock to, and showing the
          // free-choice service step first would defeat the whole point
          // (the customer must never be able to pick a different
          // service than the one being replaced).
          <div
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              backdropFilter: 'blur(20px)',
            }}
          >
            <p style={{ color: '#a78bfa', fontSize: '13.5px', lineHeight: 1.5, marginBottom: '16px', textAlign: 'center' }}>
              ⏰ {tb.earlierSlot.verifyIntro}
            </p>
            <form onSubmit={handleVerifyRebookEmail}>
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}
                >
                  {tb.earlierSlot.verifyEmailPrompt}
                </label>
                <input
                  type="email"
                  required
                  value={rebookEmailInput}
                  onChange={(e) => setRebookEmailInput(e.target.value)}
                  placeholder={tb.datetime.emailPlaceholder}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px',
                    padding: '11px 14px',
                    color: '#fff',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              {rebookError && (
                <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>{rebookError}</p>
              )}
              <button
                type="submit"
                disabled={rebookVerifying}
                style={{
                  width: '100%',
                  background: rebookVerifying ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '14px',
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: rebookVerifying ? 'not-allowed' : 'pointer',
                }}
              >
                {rebookVerifying ? tb.earlierSlot.verifyingEllipsis : tb.earlierSlot.verifyButton}
              </button>
            </form>
          </div>
        ) : (
          <>
        {rebookRef && (
          <div
            style={{
              background: 'rgba(124,58,237,0.12)',
              border: '1px solid rgba(124,58,237,0.3)',
              borderRadius: '14px',
              padding: '14px 18px',
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            <p style={{ color: '#a78bfa', fontSize: '13.5px', lineHeight: 1.5, margin: 0 }}>
              ⏰ {tb.earlierSlot.replacementNotice}
            </p>
          </div>
        )}
        {/* Progress stepper */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0',
            marginBottom: '20px',
          }}
        >
          {STEPS.map((st, i) => (
            <React.Fragment key={st}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <div
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '50%',
                    background:
                      step === st
                        ? 'linear-gradient(135deg, #7C3AED, #5D0AF4)'
                        : i < currentStepIndex
                          ? 'rgba(124,58,237,0.4)'
                          : 'rgba(255,255,255,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 700,
                    color: i < currentStepIndex ? '#a78bfa' : '#fff',
                    border:
                      step === st
                        ? 'none'
                        : i < currentStepIndex
                          ? '1px solid rgba(124,58,237,0.4)'
                          : '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  {i < currentStepIndex ? '✓' : i + 1}
                </div>
                <span
                  style={{
                    color: step === st ? '#a78bfa' : 'rgba(255,255,255,0.3)',
                    fontSize: '10px',
                    fontWeight: step === st ? 600 : 400,
                  }}
                >
                  {tb.steps[st]}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  style={{
                    width: '40px',
                    height: '2px',
                    background:
                      i < currentStepIndex ? 'rgba(124,58,237,0.4)' : 'rgba(255,255,255,0.08)',
                    margin: '0 4px 18px',
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Service — never shown for a verified replacement;
            the service is already resolved+locked by
            get_replaceable_booking_context, and step is set straight
            to 'datetime' the moment verification succeeds. */}
        {step === 'service' && !rebookRef && (
          <div
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              backdropFilter: 'blur(20px)',
            }}
          >
            <h2 style={{ color: '#fff', fontSize: '18px', fontWeight: 700, marginBottom: '6px' }}>
              {tb.service.title}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '20px' }}>
              {tb.service.subtitle}
            </p>
            {services.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '24px 0' }}>
                {tb.service.empty}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {services.map((svc) => (
                  <button
                    key={svc.id}
                    className="svc-btn"
                    onClick={() => {
                      setSelectedService(svc);
                      setStep('datetime');
                      setSelectedDate('');
                      setSelectedTime('');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 18px',
                      background:
                        selectedService?.id === svc.id
                          ? 'rgba(124,58,237,0.15)'
                          : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${selectedService?.id === svc.id ? 'rgba(124,58,237,0.5)' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: '#fff', fontWeight: 600, fontSize: '15px', margin: 0 }}>
                        {svc.name}
                      </p>
                      {svc.description && (
                        <p
                          style={{
                            color: 'rgba(255,255,255,0.5)',
                            fontSize: '12.5px',
                            margin: '4px 0 0',
                            lineHeight: 1.5,
                          }}
                        >
                          {svc.description}
                        </p>
                      )}
                      <p
                        style={{
                          color: 'rgba(255,255,255,0.4)',
                          fontSize: '13px',
                          margin: '4px 0 0',
                        }}
                      >
                        ⏱ {tb.service.durationMinutes(svc.duration_minutes)}
                      </p>
                    </div>
                    {svc.price != null && (
                      <span
                        style={{
                          color: '#a78bfa',
                          fontWeight: 700,
                          fontSize: '18px',
                          flexShrink: 0,
                          marginLeft: '12px',
                        }}
                      >
                        €{svc.price}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div
              style={{
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255,255,255,0.07)',
                textAlign: 'center',
              }}
            >
              <button
                onClick={() => setShowCancelModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.35)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {tb.service.cancelExisting}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Date & Time with full monthly calendar */}
        {step === 'datetime' && selectedService && (
          <div
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}
            >
              {/* No 'service' step exists in the replacement flow to go
                  back to — the service is locked by verification, not
                  a free choice, so this button simply doesn't render
                  for it. */}
              {!rebookRef && (
                <button
                  onClick={() => setStep('service')}
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  ← {t.common.back}
                </button>
              )}
              <h2 style={{ color: '#fff', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                {tb.datetime.title}
              </h2>
            </div>

            <div
              style={{
                background: 'rgba(124,58,237,0.1)',
                border: '1px solid rgba(124,58,237,0.2)',
                borderRadius: '10px',
                padding: '10px 14px',
                marginBottom: '20px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <span style={{ color: '#a78bfa', fontSize: '13px' }}>✂️ {selectedService.name}</span>
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>•</span>
              <span style={{ color: '#a78bfa', fontSize: '13px' }}>
                {tb.datetime.durationShort(selectedService.duration_minutes)}
              </span>
              {selectedService.price !== null && (
                <>
                  <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>•</span>
                  <span style={{ color: '#a78bfa', fontSize: '13px' }}>
                    €{selectedService.price}
                  </span>
                </>
              )}
            </div>

            {/* Full monthly calendar */}
            {(() => {
              const year = calendarMonth.getFullYear();
              const month = calendarMonth.getMonth();
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              const startDow = (firstDay.getDay() + 6) % 7;
              const totalCells = Math.ceil((startDow + lastDay.getDate()) / 7) * 7;
              // Local Y/M/D, not toISOString() — for a timezone ahead of UTC
              // (e.g. Europe/Tirane), the UTC date lags the real local date
              // for part of the early morning, which would misclassify
              // today/yesterday in the calendar grid.
              const now = new Date();
              const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

              return (
                <div style={{ marginBottom: '20px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '12px',
                    }}
                  >
                    <button
                      onClick={() => {
                        // A date selected in the month being left behind no
                        // longer corresponds to anything on screen — clear it
                        // (and its time/waitlist state) so nothing stale
                        // carries into the newly-visible month.
                        setCalendarMonth(new Date(year, month - 1, 1));
                        setSelectedDate('');
                        setSelectedTime('');
                        setShowWaitlistPrompt(false);
                        setWaitlistSuccess(false);
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.07)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      ‹
                    </button>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>
                      {tb.months[month]} {year}
                    </span>
                    <button
                      onClick={() => {
                        setCalendarMonth(new Date(year, month + 1, 1));
                        setSelectedDate('');
                        setSelectedTime('');
                        setShowWaitlistPrompt(false);
                        setWaitlistSuccess(false);
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.07)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      ›
                    </button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '3px',
                      marginBottom: '6px',
                    }}
                  >
                    {tb.weekdaysShort.map((d, idx) => (
                      <div
                        key={`${d}-${idx}`}
                        style={{
                          textAlign: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          color: 'rgba(255,255,255,0.3)',
                          padding: '4px 0',
                        }}
                      >
                        {d}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}
                  >
                    {Array.from({ length: totalCells }, (_, i) => {
                      const dayNum = i - startDow + 1;
                      if (dayNum < 1 || dayNum > lastDay.getDate()) return <div key={i} />;
                      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                      const isPast = dateStr < today;
                      // Rebook mode only: a date after the original
                      // booking's own date can never satisfy "strictly
                      // earlier" for any time on it, so it is disabled
                      // exactly like a past date — never a new visual
                      // treatment, the same isPast-driven styling below
                      // just also applies here.
                      const isAfterOriginal = Boolean(
                        rebookRef && rebookOriginal && dateStr > rebookOriginal.date
                      );
                      const isBlocked = isPast || isAfterOriginal;
                      // A date absent from availabilityMap means the RPC
                      // produced zero rows for it — closed or inside a
                      // closure range, exactly like the old
                      // schedule/closure lookup used to determine.
                      const daySlots = availabilityMap.get(dateStr);
                      const isClosed = !daySlots || daySlots.length === 0;
                      const effectiveDaySlots = getRebookEffectiveSlots(dateStr, daySlots || []);
                      const hasAvail = !isBlocked && !isClosed && effectiveDaySlots.some((sl) => sl.available);
                      const isSelected = dateStr === selectedDate;
                      const isToday = dateStr === today;

                      return (
                        <button
                          key={i}
                          className="cal-day"
                          onClick={() => {
                            if (!isBlocked && !isClosed && hasAvail) {
                              setSelectedDate(dateStr);
                              setSelectedTime('');
                              setShowWaitlistPrompt(false);
                              setWaitlistSuccess(false);
                            }
                          }}
                          disabled={isBlocked || isClosed || !hasAvail}
                          style={{
                            padding: '8px 2px',
                            borderRadius: '8px',
                            border: `1px solid ${isSelected ? 'rgba(124,58,237,0.7)' : isToday ? 'rgba(124,58,237,0.3)' : 'rgba(255,255,255,0.06)'}`,
                            background: isSelected
                              ? 'rgba(124,58,237,0.25)'
                              : isClosed || isBlocked
                                ? 'transparent'
                                : hasAvail
                                  ? 'rgba(52,211,153,0.06)'
                                  : 'rgba(239,68,68,0.04)',
                            cursor: isBlocked || isClosed || !hasAvail ? 'not-allowed' : 'pointer',
                            opacity: isBlocked || isClosed ? 0.3 : !hasAvail ? 0.5 : 1,
                            textAlign: 'center',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '13px',
                              fontWeight: isToday ? 700 : 500,
                              color: isSelected ? '#a78bfa' : isToday ? '#7C3AED' : '#fff',
                            }}
                          >
                            {dayNum}
                          </div>
                          {!isBlocked && !isClosed && (
                            <div
                              style={{
                                width: '4px',
                                height: '4px',
                                borderRadius: '50%',
                                background: hasAvail ? '#4ade80' : '#f87171',
                                margin: '2px auto 0',
                              }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div
                    style={{ display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#4ade80',
                        }}
                      />
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                        {tb.datetime.availableLegend}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#f87171',
                        }}
                      />
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                        {tb.datetime.bookedLegend}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Waitlist when no availability */}
            {selectedDateIsFullyBooked && !waitlistSuccess && (
              <div
                style={{
                  background: 'rgba(251,146,60,0.08)',
                  border: '1px solid rgba(251,146,60,0.25)',
                  borderRadius: '14px',
                  padding: '20px',
                  marginBottom: '20px',
                }}
              >
                <p
                  style={{
                    color: '#fb923c',
                    fontSize: '15px',
                    fontWeight: 600,
                    marginBottom: '4px',
                  }}
                >
                  {tb.datetime.noSlotsTitle}
                </p>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '14px',
                    marginBottom: '16px',
                    lineHeight: 1.5,
                  }}
                >
                  {tb.datetime.noSlotsQuestion}
                </p>
                {!showWaitlistPrompt ? (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setShowWaitlistPrompt(true)}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '11px',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {tb.datetime.notifyMe}
                    </button>
                    <button
                      onClick={() => setSelectedDate('')}
                      style={{
                        flex: 1,
                        background: 'rgba(255,255,255,0.07)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '10px',
                        padding: '11px',
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                    >
                      {tb.datetime.noThanks}
                    </button>
                  </div>
                ) : (
                  <div>
                    {[
                      {
                        label: tb.datetime.fullNameLabel,
                        key: 'name',
                        type: 'text',
                        placeholder: tb.datetime.fullNamePlaceholder,
                        required: true,
                      },
                      {
                        label: tb.datetime.emailLabel,
                        key: 'email',
                        type: 'email',
                        placeholder: tb.datetime.emailPlaceholder,
                        required: true,
                      },
                      {
                        label: tb.datetime.phoneOptionalLabel,
                        key: 'phone',
                        type: 'tel',
                        placeholder: tb.datetime.phonePlaceholder,
                        required: false,
                      },
                    ].map(({ label, key, type, placeholder, required }) => (
                      <div key={key} style={{ marginBottom: '10px' }}>
                        <label
                          style={{
                            display: 'block',
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: '13px',
                            marginBottom: '6px',
                          }}
                        >
                          {label}
                        </label>
                        <input
                          type={type}
                          value={waitlistForm[key as keyof typeof waitlistForm]}
                          onChange={(e) =>
                            setWaitlistForm({ ...waitlistForm, [key]: e.target.value })
                          }
                          placeholder={placeholder}
                          required={required}
                          style={{
                            width: '100%',
                            background: 'rgba(255,255,255,0.07)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            color: '#fff',
                            fontSize: '14px',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                    ))}
                    <button
                      onClick={handleWaitlistSubmit}
                      disabled={waitlistSubmitting || !waitlistForm.name || !waitlistForm.email}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        marginTop: '4px',
                      }}
                    >
                      {waitlistSubmitting ? tb.datetime.savingEllipsis : tb.datetime.addToWaitlist}
                    </button>
                  </div>
                )}
              </div>
            )}

            {waitlistSuccess && (
              <div
                style={{
                  background: 'rgba(52,211,153,0.1)',
                  border: '1px solid rgba(52,211,153,0.3)',
                  borderRadius: '14px',
                  padding: '20px',
                  marginBottom: '20px',
                  textAlign: 'center',
                }}
              >
                <p style={{ fontSize: '28px', marginBottom: '8px' }}>✅</p>
                <p
                  style={{
                    color: '#34d399',
                    fontSize: '15px',
                    fontWeight: 600,
                    marginBottom: '4px',
                  }}
                >
                  {tb.datetime.waitlistSuccessTitle}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                  {tb.datetime.waitlistSuccessBody}
                </p>
              </div>
            )}

            {/* Time slots */}
            {selectedDate && selectedDateHasAvailableSlots && (
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '14px',
                    marginBottom: '12px',
                    fontWeight: 500,
                  }}
                >
                  {tb.datetime.availableTimesLabel}
                </label>
                {timeSlots.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                    {tb.datetime.noSlotsForDay}
                  </p>
                ) : (
                  <div
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}
                  >
                    {timeSlots.map(({ time, available }) => (
                      <button
                        key={time}
                        className={available ? 'slot-btn' : ''}
                        onClick={() => available && setSelectedTime(time)}
                        disabled={!available}
                        style={{
                          padding: '10px 4px',
                          background: !available
                            ? 'rgba(239,68,68,0.08)'
                            : selectedTime === time
                              ? 'linear-gradient(135deg, #7C3AED, #5D0AF4)'
                              : 'rgba(255,255,255,0.07)',
                          border: `1px solid ${!available ? 'rgba(239,68,68,0.2)' : selectedTime === time ? 'transparent' : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: '10px',
                          color: !available ? 'rgba(255,255,255,0.2)' : '#fff',
                          fontSize: '13px',
                          fontWeight: selectedTime === time ? 600 : 400,
                          cursor: !available ? 'not-allowed' : 'pointer',
                          textDecoration: !available ? 'line-through' : 'none',
                          transition: 'all 0.15s',
                        }}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!selectedDateIsFullyBooked && (
              <button
                onClick={() => setStep('info')}
                disabled={!selectedDate || !selectedTime}
                style={{
                  width: '100%',
                  background:
                    !selectedDate || !selectedTime
                      ? 'rgba(124,58,237,0.3)'
                      : 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '14px',
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: !selectedDate || !selectedTime ? 'not-allowed' : 'pointer',
                }}
              >
                {tb.datetime.continueCta} →
              </button>
            )}
          </div>
        )}

        {/* STEP 3: Client info */}
        {step === 'info' && selectedService && (
          <div
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}
            >
              <button
                onClick={() => setStep('datetime')}
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                ← {t.common.back}
              </button>
              <h2 style={{ color: '#fff', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                {tb.info.title}
              </h2>
            </div>
            <div
              style={{
                background: 'rgba(124,58,237,0.1)',
                border: '1px solid rgba(124,58,237,0.2)',
                borderRadius: '12px',
                padding: '14px 16px',
                marginBottom: '24px',
              }}
            >
              <p style={{ color: '#a78bfa', fontSize: '13px', margin: 0 }}>
                ✂️ {selectedService.name}
                {selectedService.price != null ? ` — €${selectedService.price}` : ''}
              </p>
              <p style={{ color: '#a78bfa', fontSize: '13px', margin: '4px 0 0' }}>
                📅 {selectedDate} • {selectedTime}
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              {[
                {
                  label: tb.datetime.fullNameLabel,
                  key: 'name',
                  type: 'text',
                  placeholder: tb.datetime.fullNamePlaceholder,
                  required: true,
                },
                {
                  label: tb.datetime.emailLabel,
                  key: 'email',
                  type: 'email',
                  placeholder: tb.datetime.emailPlaceholder,
                  required: true,
                },
                {
                  label: tb.info.phoneLabel,
                  key: 'phone',
                  type: 'tel',
                  placeholder: tb.datetime.phonePlaceholder,
                  required: true,
                },
              ].map(({ label, key, type, placeholder, required }) => (
                <div key={key} style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '14px',
                      marginBottom: '8px',
                      fontWeight: 500,
                    }}
                  >
                    {label}
                    {required && <span style={{ color: '#a78bfa' }}> *</span>}
                  </label>
                  <input
                    type={type}
                    value={form[key as keyof typeof form]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    placeholder={placeholder}
                    required={required}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '10px',
                      padding: '12px 16px',
                      color: '#fff',
                      fontSize: '15px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              ))}
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}
                >
                  {tb.info.notesOptionalLabel}
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder={tb.info.notesPlaceholder}
                  rows={2}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    color: '#fff',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                  }}
                />
              </div>
              {submitError && (
                <div
                  style={{
                    background: 'rgba(239,68,68,0.15)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#fca5a5',
                    fontSize: '14px',
                  }}
                >
                  {submitError}
                </div>
              )}
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', lineHeight: 1.5, marginBottom: '12px' }}>
                {t.legal.bookingNotice.prefix}
                <Link href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'underline' }}>
                  {t.legal.bookingNotice.linkText}
                </Link>
                {t.legal.bookingNotice.suffix}
              </p>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%',
                  background: submitting
                    ? 'rgba(124,58,237,0.5)'
                    : 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '14px',
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? tb.info.sendingEllipsis : `${tb.info.confirmCta} →`}
              </button>
            </form>
          </div>
        )}
          </>
        )}

        <p
          style={{
            textAlign: 'center',
            marginTop: '24px',
            color: 'rgba(255,255,255,0.3)',
            fontSize: '12px',
          }}
        >
          {tb.poweredBy}{' '}
          <Link href="/" style={{ color: '#7C3AED', textDecoration: 'none' }}>
            Rezervo Online
          </Link>
        </p>
      </div>

      {/* Customer cancellation modal (Booking ID + email) */}
      {showCancelModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(145deg, rgba(20,12,45,0.98), rgba(10,6,25,0.99))',
              border: '1px solid rgba(124,58,237,0.3)',
              borderRadius: '20px',
              padding: '32px',
              width: '100%',
              maxWidth: '440px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>
                {tb.cancelModal.title}
              </h3>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelConfirmStep(false);
                  setCancelSuccess(false);
                  setCancelError('');
                  setFoundBooking(null);
                  setCancelBookingRef('');
                  setCancelEmail('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '20px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            {cancelSuccess ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <p style={{ fontSize: '40px', marginBottom: '12px' }}>✅</p>
                <p
                  style={{
                    color: '#34d399',
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}
                >
                  {tb.cancelModal.cancelSuccessTitle}
                </p>
                <p
                  style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '20px' }}
                >
                  {tb.cancelModal.cancelSuccessBody}
                </p>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelConfirmStep(false);
                    setCancelSuccess(false);
                    setCancelError('');
                    setFoundBooking(null);
                    setCancelBookingRef('');
                    setCancelEmail('');
                  }}
                  style={{
                    background: 'rgba(52,211,153,0.15)',
                    border: '1px solid rgba(52,211,153,0.3)',
                    borderRadius: '10px',
                    padding: '10px 24px',
                    color: '#34d399',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t.common.close}
                </button>
              </div>
            ) : cancelConfirmStep && foundBooking ? (
              <div>
                <div
                  style={{
                    background: 'rgba(248,113,113,0.08)',
                    border: '1px solid rgba(248,113,113,0.2)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                  }}
                >
                  <p
                    style={{
                      color: '#f87171',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '8px',
                    }}
                  >
                    ⚠️ {tb.cancelModal.confirmHeading}
                  </p>
                  <p
                    style={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '13px',
                      marginBottom: '4px',
                    }}
                  >
                    {tb.cancelModal.clientLabel}{' '}
                    <strong style={{ color: '#fff' }}>{foundBooking.client_name}</strong>
                  </p>
                  <p
                    style={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '13px',
                      marginBottom: '4px',
                    }}
                  >
                    {tb.cancelModal.serviceLabel}{' '}
                    <strong style={{ color: '#fff' }}>{foundBooking.service_name}</strong>
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    {tb.cancelModal.dateLabel}{' '}
                    <strong style={{ color: '#fff' }}>
                      {foundBooking.booking_date} • {foundBooking.booking_time?.substring(0, 5)}
                    </strong>
                  </p>
                </div>
                <p
                  style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '20px' }}
                >
                  {tb.cancelModal.irreversibleWarning}
                </p>
                {cancelError && (
                  <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>
                    {cancelError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setCancelConfirmStep(false)}
                    style={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '10px',
                      padding: '12px',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    {t.common.back}
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    disabled={cancelLoading}
                    style={{
                      flex: 1,
                      background: cancelLoading ? 'rgba(248,113,113,0.3)' : 'rgba(248,113,113,0.2)',
                      border: '1px solid rgba(248,113,113,0.4)',
                      borderRadius: '10px',
                      padding: '12px',
                      color: '#f87171',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: cancelLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {cancelLoading
                      ? tb.cancelModal.cancellingEllipsis
                      : tb.cancelModal.confirmCancelYes}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p
                  style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '20px' }}
                >
                  {tb.cancelModal.intro}
                </p>
                <div style={{ marginBottom: '14px' }}>
                  <label
                    style={{
                      display: 'block',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '13px',
                      marginBottom: '6px',
                    }}
                  >
                    {tb.cancelModal.bookingIdLabel}
                  </label>
                  <input
                    type="text"
                    value={cancelBookingRef}
                    onChange={(e) => setCancelBookingRef(e.target.value)}
                    placeholder={tb.cancelModal.bookingIdPlaceholder}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '10px',
                      padding: '11px 14px',
                      color: '#fff',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      display: 'block',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '13px',
                      marginBottom: '6px',
                    }}
                  >
                    {tb.cancelModal.yourEmailLabel}
                  </label>
                  <input
                    type="email"
                    value={cancelEmail}
                    onChange={(e) => setCancelEmail(e.target.value)}
                    placeholder={tb.datetime.emailPlaceholder}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '10px',
                      padding: '11px 14px',
                      color: '#fff',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                {cancelError && (
                  <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>
                    {cancelError}
                  </p>
                )}
                <button
                  onClick={handleFindBooking}
                  disabled={cancelLoading || !cancelBookingRef || !cancelEmail}
                  style={{
                    width: '100%',
                    background:
                      cancelLoading || !cancelBookingRef || !cancelEmail
                        ? 'rgba(124,58,237,0.3)'
                        : 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor:
                      cancelLoading || !cancelBookingRef || !cancelEmail
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                >
                  {cancelLoading ? tb.cancelModal.searchingEllipsis : tb.cancelModal.findBooking}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BusinessBookingPage() {
  return (
    <Suspense
      fallback={
        <div
          style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0015, #1a0035)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(124,58,237,0.3)',
              borderTop: '3px solid #7C3AED',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      }
    >
      <BusinessBookingPageContent />
    </Suspense>
  );
}
